import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
} from '@/hooks/query/useQueryProducts';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKeys } from '@/utils/translations';
import { Period, type ProductView } from '@heidi/commerce/contracts';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { type LucideIcon, type LucideProps } from 'lucide-react';
import { memo } from 'react';
import { Trans } from 'react-i18next';

interface ProductCardProps {
  title: TranslationKeys;
  variant?: 'pro' | 'free';
  details: {
    icon: LucideIcon;
    translationKey: TranslationKeys;
    iconProps?: Omit<LucideProps, 'ref'>;
  }[];
  product?: ProductView;
  ctaButton: React.ReactNode;
  period: Period;
}
function ProductCardComponent({
  details,
  product,
  ctaButton,
  title,
  period,
  variant = 'free',
}: ProductCardProps) {
  const { t, locale } = useTranslations();

  const plan = getPlanFromPeriod(period, product);
  const currencyCode = plan?.currency ?? '';
  const currencySymbol = getCurrencySymbol(currencyCode);
  const freePrice = formatPriceWithCurrency('0', currencyCode, currencySymbol);

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-lg border p-5',
        variant === 'pro' && 'border-tier-pro bg-tier-pro-muted'
      )}
    >
      <div className="flex grow flex-col gap-1">
        <TypographyP2Bold
          className={cn(variant === 'pro' ? 'text-tier-pro-text' : 'text-text-secondary')}
        >
          {t(title)}
        </TypographyP2Bold>
        <div className="flex grow items-center gap-2">
          {variant === 'pro' ? (
            <>
              <TypographyH3>
                {getDisplayedPriceFromPlan(
                  getPlanFromPeriod(period, product),
                  'month',
                  false,
                  locale
                )}
              </TypographyH3>
              <div className="flex flex-col text-text-secondary">
                <TypographyCaption>
                  {t('endTrialModal.proPlan.priceSuffix')}
                </TypographyCaption>
                <TypographyCaption>
                  {period === Period.ONE_YEAR
                    ? t('endTrialModal.proPlan.billedYearly')
                    : t('endTrialModal.proPlan.billedMonthly')}
                  {period === Period.ONE_YEAR && (
                    <>
                      {t('endTrialModal.proPlan.priceSeparator')}{' '}
                      {`${getDisplayedPriceFromPlan(
                        getPlanFromPeriod(period, product),
                        'year',
                        false,
                        locale
                      )}`}
                    </>
                  )}
                </TypographyCaption>
              </div>
            </>
          ) : (
            <TypographyH3>{freePrice}</TypographyH3>
          )}
        </div>
      </div>

      <ul className="flex flex-col gap-3">
        {details.map((detail) => {
          const { className, ...iconProps } = detail.iconProps ?? {};
          return (
            <li className="flex items-center gap-2" key={detail.translationKey}>
              <detail.icon
                className={cn(
                  'size-[18px] shrink-0',
                  variant === 'pro' ? 'text-tier-pro-text' : 'text-text-secondary',
                  className
                )}
                {...iconProps}
              />
              <span>
                <Trans
                  // cast as string to prevent weird TS bug
                  i18nKey={detail.translationKey as string}
                  components={{
                    bold: <TypographyP2Bold className="inline">{''}</TypographyP2Bold>,
                    text: <TypographyP2 className="inline">{''}</TypographyP2>,
                  }}
                />
              </span>
            </li>
          );
        })}
      </ul>

      {ctaButton}
    </div>
  );
}

export const ProductCard = memo(ProductCardComponent);
